import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaChevronLeft } from "react-icons/fa";
import { fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import {
    fetchCandidateInvites,
    fetchInviteHiringStatuses,
    resendCandidateInvite,
} from "../../Redux/ActionCreators/candidateAction";
import AddCandidatesTab from "./AddCandidatesTab";
import InvitedCandidatesTab from "./InvitedCandidatesTab";
import "./inviteCandidatesV2.scss";

const DEFAULT_SUMMARY = { invited: 0, applied: 0, no_response: 0, total: 0 };

// Standalone page mounted at /jobs/:jobId/invite. Owns the invite list + summary
// (GET /jobs/{job_id}/candidate-invites/) so candidates picked up in any of the
// three "Add candidates" flows (smart search / CSV / resumes) surface together
// in "Invited candidates". CSV/resume uploads flagged "already applied" go
// straight to job candidates and intentionally do not appear here.
const InviteCandidatesV2 = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const job = useSelector((state) => state.jobs.latestJob);
    const [titleLoading, setTitleLoading] = useState(false);

    const [activeMainTab, setActiveMainTab] = useState("add"); // 'add' | 'invited'

    // ── Invite list state (server-driven) ───────────────────────────────────
    const [invites, setInvites] = useState([]);
    const [summary, setSummary] = useState(DEFAULT_SUMMARY);
    const [invitesLoading, setInvitesLoading] = useState(false);
    const [hiringStatuses, setHiringStatuses] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [statusId, setStatusId] = useState("");
    const [pagination, setPagination] = useState(null);

    useEffect(() => {
        const fetchJobData = async () => {
            if (!jobId) return;
            setTitleLoading(true);
            await dispatch(fetchJobById(jobId));
            setTitleLoading(false);
        };
        fetchJobData();
    }, [dispatch, jobId]);

    useEffect(() => {
        let active = true;
        dispatch(fetchInviteHiringStatuses()).then((res) => {
            if (active && res?.success) setHiringStatuses(res.data || []);
        });
        return () => { active = false; };
    }, [dispatch]);

    const loadInvites = useCallback(async () => {
        if (!jobId) return;
        setInvitesLoading(true);
        const res = await dispatch(fetchCandidateInvites(jobId, { statusId: statusId || undefined, page, pageSize }));
        setInvitesLoading(false);
        if (res?.success) {
            setInvites(res.invites || []);
            setSummary(res.summary || DEFAULT_SUMMARY);
            setPagination(res.pagination || null);
        }
    }, [dispatch, jobId, statusId, page, pageSize]);

    useEffect(() => {
        loadInvites();
    }, [loadInvites]);

    // Called by every add flow once its API call resolves. Resets to page 1 so
    // the newest invites are visible, then refetches list + summary.
    const handleInvited = useCallback(() => {
        setPage(1);
        // If already on page 1 the loadInvites effect won't re-run on its own.
        loadInvites();
    }, [loadInvites]);

    const goToCandidates = useCallback(() => {
        navigate(`/jobs/${jobId}/candidates`);
    }, [navigate, jobId]);

    const handleResend = useCallback(async (invite) => {
        const inviteId = invite.id || invite.invite_id;
        const res = await dispatch(resendCandidateInvite(jobId, inviteId));
        if (res?.success) loadInvites();
        return res;
    }, [dispatch, jobId, loadInvites]);

    const invitedEmails = new Set(
        invites
            .map((inv) => (inv.candidate?.email || inv.candidate_email || "").toLowerCase())
            .filter(Boolean)
    );
    const companyName = job?.company_name || job?.organization?.name || job?.company || "";
    const totalCount = summary?.total ?? invites.length;

    return (
        <div className="icv2-wrap">
            <div className="icv2-header-bar">
                <button className="icv2-back-arrow" onClick={goToCandidates} title="Back to Candidates">
                    <FaChevronLeft />
                </button>

                <span className="icv2-eyebrow">Candidate intake workspace</span>

                {titleLoading ? (
                    <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : (
                    <>
                        <h1 className="icv2-workspace-title">{job?.title || "Untitled role"}</h1>
                        <p className="icv2-workspace-subtitle">
                            {companyName || "Your hiring team"}
                            <span>·</span>
                            Invite and track outreach
                        </p>
                    </>
                )}
{/*
                <button className="icv2-view-pipeline-link" onClick={goToCandidates}>
                    View pipeline →
                </button> */}
            </div>

            <div className="icv2-content">
                <div className="icv2-tabbar">
                    <button
                        className={`icv2-tabbar-btn${activeMainTab === "add" ? " icv2-tabbar-btn--active" : ""}`}
                        onClick={() => setActiveMainTab("add")}
                    >
                        Add candidates
                    </button>
                    <button
                        className={`icv2-tabbar-btn${activeMainTab === "invited" ? " icv2-tabbar-btn--active" : ""}`}
                        onClick={() => { setActiveMainTab("invited"); loadInvites(); }}
                    >
                        Invited candidates
                        <span className="icv2-tab-badge">{totalCount}</span>
                    </button>
                </div>

                <div className="icv2-tabpanel">
                    {activeMainTab === "add" ? (
                        <AddCandidatesTab
                            jobId={jobId}
                            jobTitle={job?.title}
                            invitedCount={totalCount}
                            invitedEmails={invitedEmails}
                            onInvited={handleInvited}
                        />
                    ) : (
                        <InvitedCandidatesTab
                            jobId={jobId}
                            invites={invites}
                            summary={summary}
                            loading={invitesLoading}
                            hiringStatuses={hiringStatuses}
                            page={page}
                            pageSize={pageSize}
                            statusId={statusId}
                            pagination={pagination}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                            onStatusChange={(id) => { setStatusId(id); setPage(1); }}
                            onResend={handleResend}
                            onRefresh={loadInvites}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default InviteCandidatesV2;
